import React from 'react'
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    Fab,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import {
    Check as CheckIcon,
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    Tune as TuneIcon,
} from '@mui/icons-material'
import {
    applyMswScenario,
    clearMswScenarios,
    clearMswOperationNames,
    clearMswOperationResponses,
    clearMswOverride,
    deleteMswScenario,
    replaceMswOverrides,
    saveMswScenario,
    setMswEnabled,
    setMswOverride,
    useMswEnabled,
    useMswOperationNames,
    useMswOperationResponses,
    useMswOverrides,
    useMswScenarios,
} from '@edge/spark/io-roles'
import { clearApolloCache } from '@edge/spark/io-apollo'

type JsonPrimitive = string | number | boolean | null

const MSW_STATE_QUERY_PARAM = 'mswState'
const MSW_SHARE_VERSION = 1

interface MswSharePayload {
    v: number
    enabled: boolean
    overrides: Record<string, string>
    scenarioName?: string
}

interface JsonLeafField {
    id: string
    path: Array<string | number>
    value: JsonPrimitive
}

const toPathLabel = (path: Array<string | number>): string => {
    if (path.length === 0) {
        return '(root)'
    }

    return path
        .map((segment) => (typeof segment === 'number' ? `[${segment}]` : segment))
        .join('.')
        .replace('.[', '[')
}

const collectJsonLeafFields = (input: unknown, path: Array<string | number> = []): JsonLeafField[] => {
    if (input === null || typeof input === 'string' || typeof input === 'number' || typeof input === 'boolean') {
        return [{ id: toPathLabel(path), path, value: input }]
    }

    if (Array.isArray(input)) {
        return input.flatMap((item, index) => collectJsonLeafFields(item, [...path, index]))
    }

    if (typeof input === 'object') {
        return Object.entries(input).flatMap(([key, value]) => collectJsonLeafFields(value, [...path, key]))
    }

    return []
}

const parseByType = (raw: string, current: JsonPrimitive): JsonPrimitive => {
    if (current === null) {
        return raw.trim().toLowerCase() === 'null' ? null : raw
    }

    if (typeof current === 'number') {
        const n = Number(raw)
        return Number.isNaN(n) ? current : n
    }

    if (typeof current === 'boolean') {
        if (raw.trim().toLowerCase() === 'true') return true
        if (raw.trim().toLowerCase() === 'false') return false
        return current
    }

    return raw
}

const setJsonValueAtPath = (
    root: Record<string, unknown> | unknown[],
    path: Array<string | number>,
    value: JsonPrimitive,
): void => {
    if (path.length === 0) return

    let target: unknown = root
    for (let i = 0; i < path.length - 1; i++) {
        const segment = path[i]
        target =
            typeof segment === 'number' ? (target as unknown[])[segment] : (target as Record<string, unknown>)[segment]
    }

    const leaf = path[path.length - 1]
    if (typeof leaf === 'number') {
        ;(target as unknown[])[leaf] = value
    } else {
        ;(target as Record<string, unknown>)[leaf] = value
    }
}

const encodeBase64Url = (value: string): string => {
    const bytes = new TextEncoder().encode(value)
    let binary = ''
    bytes.forEach((b) => {
        binary += String.fromCharCode(b)
    })
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const decodeBase64Url = (value: string): string => {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
    return new TextDecoder().decode(bytes)
}

const parseMswSharePayload = (raw: string): MswSharePayload | null => {
    try {
        const decoded = decodeBase64Url(raw)
        const payload = JSON.parse(decoded) as Partial<MswSharePayload>
        if (
            payload.v !== MSW_SHARE_VERSION ||
            typeof payload.enabled !== 'boolean' ||
            !payload.overrides ||
            typeof payload.overrides !== 'object' ||
            Array.isArray(payload.overrides)
        ) {
            return null
        }

        const normalizedOverrides: Record<string, string> = {}
        for (const [key, value] of Object.entries(payload.overrides)) {
            if (typeof key === 'string' && typeof value === 'string') {
                normalizedOverrides[key] = value
            }
        }

        return {
            v: MSW_SHARE_VERSION,
            enabled: payload.enabled,
            overrides: normalizedOverrides,
            scenarioName: typeof payload.scenarioName === 'string' ? payload.scenarioName : undefined,
        }
    } catch {
        return null
    }
}

export const MswOverlayPanel: React.VFC = () => {
    const mswEnabled = useMswEnabled()
    const mswOverrides = useMswOverrides()
    const mswScenarios = useMswScenarios()
    const mswOperationNames = useMswOperationNames()
    const mswOperationResponses = useMswOperationResponses()

    const [panelOpen, setPanelOpen] = React.useState(false)
    const [opName, setOpName] = React.useState('')
    const [opJson, setOpJson] = React.useState('')
    const [jsonError, setJsonError] = React.useState('')
    const [pathFilter, setPathFilter] = React.useState('')
    const [showRawJson, setShowRawJson] = React.useState(false)
    const [jsonCopied, setJsonCopied] = React.useState(false)
    const [scenarioName, setScenarioName] = React.useState('')
    const [shareStatus, setShareStatus] = React.useState('')

    const reportShareStatus = React.useCallback((message: string) => {
        setShareStatus(message)
        setTimeout(() => setShareStatus(''), 3000)
    }, [])

    React.useEffect(() => {
        const currentUrl = new URL(window.location.href)
        const rawState = currentUrl.searchParams.get(MSW_STATE_QUERY_PARAM)
        if (!rawState) {
            return
        }

        const payload = parseMswSharePayload(rawState)
        if (!payload) {
            reportShareStatus('Invalid share link state.')
            currentUrl.searchParams.delete(MSW_STATE_QUERY_PARAM)
            window.history.replaceState({}, '', currentUrl.toString())
            return
        }

        replaceMswOverrides(payload.overrides)
        setMswEnabled(payload.enabled)
        if (payload.scenarioName) {
            setScenarioName(payload.scenarioName)
        }

        reportShareStatus('Imported shared MSW state.')
        currentUrl.searchParams.delete(MSW_STATE_QUERY_PARAM)
        window.history.replaceState({}, '', currentUrl.toString())
    }, [reportShareStatus])

    React.useEffect(() => {
        if (mswEnabled) {
            import('../../mocks/browser').then(({ startMockWorker }) => {
                startMockWorker().catch((err) => {
                    console.error('Failed to start MSW:', err)
                })
            })
        }
    }, [mswEnabled])

    const handleSelectOperation = React.useCallback(
        (name: string) => {
            setOpName(name)
            setJsonError('')
            setPathFilter('')
            const nextJson = mswOverrides[name] ?? mswOperationResponses[name] ?? ''
            setOpJson(nextJson)
        },
        [mswOperationResponses, mswOverrides],
    )

    const parsedJson = React.useMemo(() => {
        if (!opJson.trim()) return null
        try {
            return JSON.parse(opJson)
        } catch {
            return null
        }
    }, [opJson])

    const editableFields = React.useMemo(() => {
        if (parsedJson === null || typeof parsedJson !== 'object') return []
        return collectJsonLeafFields(parsedJson)
    }, [parsedJson])

    const filteredEditableFields = React.useMemo(() => {
        const needle = pathFilter.trim().toLowerCase()
        if (!needle) return editableFields
        return editableFields.filter((field) => {
            const path = toPathLabel(field.path).toLowerCase()
            const value = String(field.value).toLowerCase()
            return path.includes(needle) || value.includes(needle)
        })
    }, [editableFields, pathFilter])

    const handleSaveOverride = () => {
        try {
            JSON.parse(opJson)
            setMswOverride(opName.trim(), opJson.trim())
            setOpName('')
            setOpJson('')
            setJsonError('')
        } catch {
            setJsonError('Invalid JSON')
        }
    }

    const handleCopyJson = async (): Promise<void> => {
        if (!opJson.trim()) return
        try {
            await navigator.clipboard.writeText(opJson)
            setJsonCopied(true)
            setTimeout(() => setJsonCopied(false), 1500)
        } catch {
            setJsonCopied(false)
        }
    }

    const handleEditField = (field: JsonLeafField, nextRawValue: string): void => {
        if (parsedJson === null || typeof parsedJson !== 'object') return
        const nextJson = JSON.parse(JSON.stringify(parsedJson)) as Record<string, unknown> | unknown[]
        setJsonValueAtPath(nextJson, field.path, parseByType(nextRawValue, field.value))
        setOpJson(JSON.stringify(nextJson, null, 2))
        setJsonError('')
    }

    const handleClearCache = () => {
        void clearApolloCache().then(() => {
            window.location.reload()
        })
    }

    const handleCopyShareLink = async (): Promise<void> => {
        const payload: MswSharePayload = {
            v: MSW_SHARE_VERSION,
            enabled: mswEnabled,
            overrides: mswOverrides,
            scenarioName: scenarioName.trim() || undefined,
        }

        try {
            const encoded = encodeBase64Url(JSON.stringify(payload))
            const url = new URL(window.location.href)
            url.searchParams.set(MSW_STATE_QUERY_PARAM, encoded)
            await navigator.clipboard.writeText(url.toString())
            reportShareStatus('Share link copied.')
        } catch {
            reportShareStatus('Failed to copy share link.')
        }
    }

    if (!mswEnabled) return null

    return (
        <>
            {/* Floating action button */}
            {!panelOpen && (
                <Tooltip title="Open GraphQL response testing tools (EdgeAdmin only)" placement="left">
                    <Fab
                        size="medium"
                        color="primary"
                        aria-label="Open GraphQL response testing tools"
                        onClick={() => setPanelOpen(true)}
                        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1400 }}
                    >
                        <TuneIcon />
                    </Fab>
                </Tooltip>
            )}

            {/* Overlay panel */}
            {panelOpen && (
                <Paper
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        width: 380,
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 1400,
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.25, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                    >
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <TuneIcon fontSize="small" color="primary" />
                            <Box>
                                <Typography variant="subtitle2">GraphQL Response Testing Tools</Typography>
                                <Chip
                                    label="EdgeAdmin only"
                                    size="small"
                                    color="warning"
                                    sx={{ mt: 0.5, height: 20 }}
                                />
                            </Box>
                        </Stack>
                        <IconButton size="small" aria-label="Close panel" onClick={() => setPanelOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    {/* Scrollable body */}
                    <Box sx={{ overflowY: 'auto', flex: 1, p: 2 }}>
                        {/* Seen operations */}
                        <Box sx={{ mb: 1.5 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Seen GraphQL operations
                                </Typography>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                        clearMswOperationNames()
                                        clearMswOperationResponses()
                                    }}
                                    disabled={mswOperationNames.length === 0}
                                >
                                    Clear
                                </Button>
                            </Stack>
                            {mswOperationNames.length > 0 ? (
                                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                    {mswOperationNames.map((name) => (
                                        <Chip
                                            key={name}
                                            label={name}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleSelectOperation(name)}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    No operations captured yet. Navigate the app while MSW is enabled.
                                </Typography>
                            )}
                        </Box>

                        {/* Active overrides */}
                        {Object.keys(mswOverrides).length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.75 }}
                                >
                                    Active overrides
                                </Typography>
                                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                    {Object.entries(mswOverrides).map(([name]) => (
                                        <Chip
                                            key={name}
                                            label={name}
                                            size="small"
                                            onClick={() => handleSelectOperation(name)}
                                            onDelete={() => clearMswOverride(name)}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Scenario library */}
                        <Box sx={{ mb: 1.5 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Scenario library
                                </Typography>
                                <Stack direction="row" spacing={0.75}>
                                    <Button size="small" variant="text" onClick={() => void handleCopyShareLink()}>
                                        Copy link
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={clearMswScenarios}
                                        disabled={Object.keys(mswScenarios).length === 0}
                                    >
                                        Clear
                                    </Button>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
                                {Object.keys(mswScenarios).length > 0 ? (
                                    Object.entries(mswScenarios).map(([name, scenarioOverrides]) => (
                                        <Chip
                                            key={name}
                                            label={`${name} (${Object.keys(scenarioOverrides).length})`}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            onClick={() => {
                                                applyMswScenario(name)
                                                setScenarioName(name)
                                            }}
                                            onDelete={() => deleteMswScenario(name)}
                                        />
                                    ))
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        Save current overrides as reusable scenarios.
                                    </Typography>
                                )}
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Scenario name"
                                    placeholder="e.g. Missing buyer fee"
                                    value={scenarioName}
                                    onChange={(e) => setScenarioName(e.target.value)}
                                />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={!scenarioName.trim() || Object.keys(mswOverrides).length === 0}
                                    onClick={() => saveMswScenario(scenarioName)}
                                >
                                    Save
                                </Button>
                            </Stack>
                            {shareStatus && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {shareStatus}
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ mb: 1.5 }} />

                        {/* Editor */}
                        <Stack spacing={1.25}>
                            <TextField
                                size="small"
                                label="Operation name"
                                placeholder="e.g. getVehicleDetail"
                                value={opName}
                                onChange={(e) => setOpName(e.target.value)}
                            />

                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Button
                                    size="small"
                                    variant="text"
                                    sx={{ alignSelf: 'flex-start' }}
                                    onClick={() => setShowRawJson((prev) => !prev)}
                                >
                                    {showRawJson ? 'Hide raw JSON' : 'Show raw JSON'}
                                </Button>
                                <IconButton
                                    size="small"
                                    aria-label="Copy raw JSON"
                                    onClick={() => {
                                        void handleCopyJson()
                                    }}
                                    disabled={!opJson.trim()}
                                >
                                    {jsonCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Stack>

                            <Collapse in={showRawJson}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Response JSON"
                                    placeholder='{"data": {...}}'
                                    multiline
                                    minRows={6}
                                    value={opJson}
                                    onChange={(e) => {
                                        setOpJson(e.target.value)
                                        setJsonError('')
                                    }}
                                    error={!!jsonError}
                                    helperText={jsonError}
                                />
                            </Collapse>

                            {editableFields.length > 0 && (
                                <Box
                                    sx={{
                                        border: (theme) => `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        p: 1,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mb: 1, display: 'block' }}
                                    >
                                        Quick field editor
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Filter fields"
                                        placeholder="path or value"
                                        value={pathFilter}
                                        onChange={(e) => setPathFilter(e.target.value)}
                                        sx={{ mb: 1 }}
                                    />
                                    <Stack spacing={1} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {filteredEditableFields.slice(0, 60).map((field) => (
                                            <Box key={field.id}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mb: 0.5, wordBreak: 'break-all' }}
                                                >
                                                    {toPathLabel(field.path)}
                                                </Typography>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={field.value === null ? 'null' : String(field.value)}
                                                    onChange={(e) => handleEditField(field, e.target.value)}
                                                />
                                            </Box>
                                        ))}
                                    </Stack>
                                    {filteredEditableFields.length === 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            No matching fields.
                                        </Typography>
                                    )}
                                    {filteredEditableFields.length > 60 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 1, display: 'block' }}
                                        >
                                            Showing first 60 of {filteredEditableFields.length} matches.
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Button
                                size="small"
                                variant="outlined"
                                disabled={!opName.trim() || !opJson.trim()}
                                onClick={handleSaveOverride}
                            >
                                Save override
                            </Button>

                            <Divider />

                            <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                fullWidth
                                onClick={handleClearCache}
                            >
                                Clear Apollo cache (+ reload)
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            )}
        </>
    )
}
